(() => {
  const one = (selector, scope = document) => scope.querySelector(selector);
  const all = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const body = document.body;
  const legacy = { '#/objects': '/collections/collected-objects', '#/product/eagle': '/products/two-eagles-rising-vintage-asian-carved-eagle-sculpture', '#/about': '/pages/our-selection', '#/': '/' };
  if (legacy[location.hash]) {
    const next = new URL(legacy[location.hash], location.origin);
    next.search = location.search;
    location.replace(next.href);
    return;
  }
  const toast = message => {
    const element = one('[data-site-status]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(element.hideTimer);
    element.hideTimer = setTimeout(() => element.classList.remove('is-visible'), 3200);
  };
  let saved = [];
  try { saved = JSON.parse(localStorage.getItem('brusion-saved-objects') || '[]'); } catch {}
  if (!Array.isArray(saved)) saved = [];
  const syncSaved = () => all('[data-save-object]').forEach(button => button.setAttribute('aria-pressed', String(saved.includes(button.dataset.saveObject))));
  const announceSaved = (id, handle) => document.dispatchEvent?.(new CustomEvent('brusion:saved-changed', { detail: { saved: [...saved], id, handle } }));
  syncSaved();
  all('[data-save-object]').forEach(button => button.addEventListener('click', () => {
    const id = button.dataset.saveObject;
    const removing = saved.includes(id);
    saved = removing ? saved.filter(value => value !== id) : [...saved, id];
    let stored = true;
    try { localStorage.setItem('brusion-saved-objects', JSON.stringify(saved)); } catch { stored = false; }
    syncSaved();
    announceSaved(id, button.dataset.saveHandle);
    toast(stored ? (removing ? body.dataset.removed : body.dataset.saved) : body.dataset.storageUnavailable);
  }));
  document.addEventListener('brusion:remove-saved', event => {
    const id = String(event.detail?.id || '');
    saved = saved.filter(value => value !== id);
    try { localStorage.setItem('brusion-saved-objects', JSON.stringify(saved)); } catch {}
    syncSaved();
    announceSaved(id);
  });
  window.addEventListener?.('storage', event => {
    if (event.key !== 'brusion-saved-objects') return;
    try { const list = JSON.parse(event.newValue || '[]'); saved = Array.isArray(list) ? list : []; } catch { saved = []; }
    syncSaved();
    announceSaved();
  });
  all('[data-gallery]').forEach(gallery => {
    const track = one('[data-gallery-track]', gallery);
    const slides = all('[data-gallery-slide]', gallery);
    const thumbs = all('[data-gallery-index]', gallery);
    const counter = one('[data-gallery-counter]', gallery);
    if (!track || !slides.length) return;
    let current = 0;
    let frame = 0;
    const sync = () => {
      current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
      if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
      const image = one('img', slides[current]);
      const ratio = image?.naturalWidth && image?.naturalHeight ? image.naturalWidth / image.naturalHeight : Number(image?.getAttribute('width')) / Number(image?.getAttribute('height'));
      if (ratio > 0) gallery.style.setProperty('--b-gallery-ratio', ratio);
      all('[data-gallery-step]', gallery).forEach(button => { button.disabled = Number(button.dataset.galleryStep) < 0 ? current === 0 : current === slides.length - 1; });
      thumbs.forEach((thumb, index) => thumb.setAttribute('aria-current', String(index === current)));
    };
    const go = index => track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, index)) * track.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
    track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(sync); }, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      go(current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    thumbs.forEach(thumb => thumb.addEventListener('click', () => go(Number(thumb.dataset.galleryIndex))));
    all('[data-gallery-step]', gallery).forEach(button => button.addEventListener('click', () => go(current + Number(button.dataset.galleryStep))));
    slides.forEach(slide => one('img', slide)?.addEventListener('load', sync));
    new ResizeObserver(() => { track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' }); sync(); }).observe(track);
    sync();
  });
  all('[data-campaign]').forEach(campaign => {
    const track = one('[data-campaign-track]', campaign);
    const slides = all('[data-campaign-slide]', campaign);
    const dots = all('[data-campaign-index]', campaign);
    const toggle = one('[data-campaign-toggle]', campaign);
    const icon = one('[data-campaign-toggle-icon]', campaign);
    const counter = one('[data-campaign-counter]', campaign);
    if (!track || slides.length < 1) return;
    let current = 0;
    let timer = 0;
    let frame = 0;
    let paused = campaign.dataset.autoplay !== 'true' || reduced;
    let hovered = false;
    let visible = true;
    const interval = Math.max(5000, Number(campaign.dataset.interval) || 7000);
    const sync = () => {
      current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      slides.forEach((slide, index) => {
        slide.inert = index !== current;
        slide.setAttribute('aria-hidden', String(index !== current));
      });
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
      if (counter) counter.textContent = String(current + 1).padStart(2, '0') + ' / ' + String(slides.length).padStart(2, '0');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(!paused));
        toggle.setAttribute('aria-label', paused ? campaign.dataset.playLabel : campaign.dataset.pauseLabel);
      }
      if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
    };
    const go = index => {
      const next = (index + slides.length) % slides.length;
      track.scrollTo({ left: next * track.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
    };
    const schedule = () => {
      clearTimeout(timer);
      if (paused || hovered || !visible || document.hidden || reduced || slides.length < 2) return;
      timer = setTimeout(() => { go(current + 1); schedule(); }, interval);
    };
    const stop = () => { paused = true; clearTimeout(timer); sync(); };
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    }, { passive: true });
    track.addEventListener('pointerdown', stop, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      stop();
      go(current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    all('[data-campaign-step]', campaign).forEach(button => button.addEventListener('click', () => {
      stop();
      go(current + Number(button.dataset.campaignStep));
    }));
    dots.forEach(dot => dot.addEventListener('click', () => { stop(); go(Number(dot.dataset.campaignIndex)); }));
    campaign.addEventListener('focusin', event => { if (event.target !== toggle) stop(); });
    campaign.addEventListener('mouseenter', () => { hovered = true; schedule(); });
    campaign.addEventListener('mouseleave', () => { hovered = false; schedule(); });
    toggle?.addEventListener('click', () => { paused = !paused; sync(); schedule(); });
    document.addEventListener('visibilitychange', schedule);
    document.addEventListener('shopify:block:select', event => {
      const index = slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
      if (index !== -1) { stop(); go(index); }
    });
    new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }, { threshold: 0.2 }).observe(campaign);
    new ResizeObserver(() => { track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' }); sync(); }).observe(track);
    sync();
    schedule();
  });
  all('[data-catalogue-filters]').forEach(form => {
    form.addEventListener('submit', event => {
      event.preventDefault();
      const category = one('[data-filter-category]', form);
      const period = one('[data-filter-period]', form);
      const price = one('[data-filter-price]', form);
      const valid = select => [...select.options].some(option => option.value === select.value) ? select.value : '';
      const tags = [valid(category), valid(period)].filter(Boolean);
      const base = form.dataset.baseHandle === 'lucky-finds' ? 'lucky-finds' : 'collected-objects';
      const next = new URL('/collections/' + base + (tags.length ? '/' + tags.map(encodeURIComponent).join('+') : ''), location.origin);
      const order = valid(price);
      if (['created-descending', 'price-ascending', 'price-descending'].includes(order)) next.searchParams.set('sort_by', order);
      location.assign(next.href);
    });
  });
  all('[data-auto-submit]').forEach(select => select.addEventListener('change', () => select.form?.requestSubmit()));
  const variant = one('[data-product-variant]');
  variant?.addEventListener('change', () => {
    const option = variant.selectedOptions[0];
    all('[data-variant-price]').forEach(price => { price.textContent = option.dataset.price; });
    const compare = one('[data-variant-compare]');
    if (compare) {
      compare.textContent = option.dataset.comparePrice || '';
      compare.hidden = option.dataset.discounted !== 'true';
    }
    document.dispatchEvent(new CustomEvent('brusion:variant-changed'));
  });
  const params = new URLSearchParams(location.search);
  const reference = one('[data-contact-reference]');
  if (reference) {
    reference.value = (params.get('ref') || '').slice(0, 80);
    const title = one('[data-contact-object]');
    if (params.get('object') && title) {
      title.textContent = params.get('object').slice(0, 250);
      title.hidden = false;
    }
    try {
      const url = new URL(params.get('url'), location.origin);
      if (url.origin === location.origin && url.pathname.startsWith('/products/')) one('[data-contact-url]').value = url.href;
    } catch {}
  }
  const menu = one('[data-mobile-menu]');
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && menu) menu.open = false; });
  document.addEventListener('click', event => { if (menu && !menu.contains(event.target)) menu.open = false; });
  let introSeen = true;
  try { introSeen = sessionStorage.getItem('brusion-logo-intro-seen-v1') === '1'; } catch {}
  if (body.classList.contains('b-template-index') && !reduced && !window.Shopify?.designMode && (!introSeen || params.has('replay-logo'))) {
    const overlay = document.createElement('div');
    overlay.className = 'materge-logo-intro';
    overlay.setAttribute('aria-hidden', 'true');
    const image = new Image();
    image.alt = '';
    image.src = body.dataset.logoMotion;
    overlay.append(image);
    document.body.append(overlay);
    try { sessionStorage.setItem('brusion-logo-intro-seen-v1', '1'); } catch {}
    setTimeout(() => overlay.remove(), 2800);
  }
})();
